import type { ShellGuidanceScope, ShellGuidanceTopic } from './kernel-contract.js';
import { predicateParses, rowMatches } from './predicate.js';
import { guidanceProseWords } from './guidance-prose.js';

/**
 * CAOS-150 — resolving guidance TOPICS: which articles are read beside this screen, which the
 * reader may see at all, and whose article wins when the org has written its own.
 *
 * This is a module of its own rather than four more closures inside `buildGuidancePanel` because
 * TWO readers need the same answers and must not drift: the list the panel draws beside a screen,
 * and the panel's search. The design this is built to says so in as many words — the resolved
 * guidance is "one script, two readers" — and the two readers here differ on exactly one axis,
 * which is the whole substance of the feature:
 *
 *   • the LIST honours `scope` (relevance) and `visibility` (permission), and applies superseding
 *   • SEARCH ignores `scope`, honours `visibility`, and ignores superseding
 *
 * Searching past scope is a feature: the reader asked for something not on this screen, which is
 * precisely when a guide is most useful, and it is the second of this ticket's criteria. Searching
 * past visibility would be a disclosure. Keeping both rules in one file is what stops a later
 * change to one of them silently becoming a change to the other.
 */

/**
 * The surface a reader is looking at, in the axes a topic's `scope` may name.
 *
 * Every field is optional because the frame genuinely may not know one: the Atlantis AI home has no
 * active app, a tab set has no object, and a surface the frame has not classified has no placement.
 * An ABSENT axis never satisfies a scope entry that names it — see {@link scopeEntryMatches}.
 */
export interface GuidanceSurface {
  readonly app?: string;
  readonly object?: string;
  readonly page?: string;
  readonly placement?: string;
}

/**
 * The reader, as the predicate vocabulary sees them: a flat record of `field == "value"` facts.
 *
 * `role` is the one fact the frame can actually supply today — the role in force, which is the
 * previewed role when somebody is previewing and their first role otherwise. That is a deliberate
 * consequence rather than a shortcut: previewing as a role already changes what the workspace shows,
 * so it should change which articles come with it, and a guide that ignored the preview would be
 * describing a workspace the previewer is not looking at.
 *
 * It is a record rather than a fixed shape so that a topic can be authored against a fact the frame
 * learns how to supply later, without this signature or any authored `visibility` changing.
 */
export type GuidanceReader = Readonly<Record<string, unknown>>;

/**
 * Does ONE scope entry describe this surface?
 *
 * Every axis the entry DECLARES has to match. An axis the surface does not know about fails rather
 * than passes: a topic scoped to `object: 'order'` must not appear on a screen where the frame
 * cannot tell which object is on display, because "we do not know" is not "yes".
 *
 * An entry declaring nothing cannot occur — the kernel schema refuses it — and would match
 * everything if it did. It is treated as matching nothing here instead, so that a body no deploy
 * could have stored cannot make a topic appear on every screen in the org.
 */
function scopeEntryMatches(entry: ShellGuidanceScope, surface: GuidanceSurface): boolean {
  const axes: (keyof ShellGuidanceScope)[] = ['app', 'object', 'page', 'placement'];
  const declared = axes.filter((axis) => entry[axis] !== undefined);
  if (declared.length === 0) return false;
  return declared.every((axis) => surface[axis] !== undefined && surface[axis] === entry[axis]);
}

/**
 * Is this topic FOR the surface being viewed?
 *
 * No `scope` at all means every surface, which is the ordinary case for the articles a guide opens
 * with: how to get around, how to sign out, what the apps are. Those are not about one screen and
 * should not have to enumerate every screen to say so.
 */
export function topicInScope(topic: ShellGuidanceTopic, surface: GuidanceSurface): boolean {
  if (topic.scope === undefined || topic.scope.length === 0) return true;
  return topic.scope.some((entry) => scopeEntryMatches(entry, surface));
}

/**
 * May this reader see this topic at all?
 *
 * Shares the workspace's one predicate vocabulary (`predicate.ts`) and differs from it on ONE
 * point, deliberately: an unreadable predicate HIDES the topic, where an unreadable `appliesWhen`
 * shows the control.
 *
 * Both directions are the safe one for what they guard. A row action that fails open is a button
 * somebody may turn out not to be allowed to press, and the write behind it is still checked; an
 * article that fails open IS the thing being protected — there is no second gate behind the prose.
 * So a typo in an admin-only topic's predicate must not publish it to everyone.
 *
 * A predicate that reads cleanly but names a fact the reader's context does not carry resolves to
 * the empty string and compares false, so it hides too. That is the same answer for the same
 * reason, and it means a topic is visible only when something actually said so.
 */
export function topicVisibleTo(topic: ShellGuidanceTopic, reader: GuidanceReader): boolean {
  if (topic.visibility === undefined) return true;
  if (!predicateParses(topic.visibility)) return false;
  return rowMatches(topic.visibility, reader as Record<string, unknown>);
}

/** Customer first: the org's own article above the platform's, with authored order inside each. */
function customerFirst(topics: readonly ShellGuidanceTopic[]): ShellGuidanceTopic[] {
  // `sort` is stable, so topics of equal rank keep the order they were projected in — which for a
  // platform topic is the authoring package's own `roots` order.
  return [...topics].sort((a, b) => {
    const rank = (a.platform ? 1 : 0) - (b.platform ? 1 : 0);
    if (rank !== 0) return rank;
    const ao = a.order ?? Number.MAX_SAFE_INTEGER;
    const bo = b.order ?? Number.MAX_SAFE_INTEGER;
    return ao === bo ? 0 : ao - bo;
  });
}

/**
 * The articles read BESIDE this screen: in scope, permitted, customer's own first, and with any
 * platform article the customer has stood in front of taken out.
 *
 * SUPERSEDING ONLY BITES WHERE THE TWO OVERLAP, which is this ticket's third criterion and the one
 * detail that makes the feature honest. The hiding is decided by whether the SUPERSEDING topic is
 * in scope here — so a customer who writes a replacement for one screen has replaced it on that
 * screen, and the platform's article still stands everywhere else it was scoped to. A customer who
 * meant to replace it everywhere scopes their own topic to everywhere, which is what omitting
 * `scope` already means.
 *
 * Only a TENANT topic supersedes. A platform topic declaring `supersedes` is naming a sibling its
 * own package ships, which is that package's business to get right at authoring time, and letting
 * it hide things here would make an install able to remove an article the org is reading with no
 * record of it anywhere. The kernel's reference check already refuses a `supersedes` naming a topic
 * that does not exist, so the key being present means the target is real either way.
 */
export function topicsForSurface(
  topics: readonly ShellGuidanceTopic[],
  surface: GuidanceSurface,
  reader: GuidanceReader,
): readonly ShellGuidanceTopic[] {
  const permitted = topics.filter((t) => topicVisibleTo(t, reader));
  const here = permitted.filter((t) => topicInScope(t, surface));
  const superseded = new Set(
    here.flatMap((t) => (!t.platform && t.supersedes !== undefined ? [t.supersedes] : [])),
  );
  return customerFirst(here.filter((t) => !superseded.has(t.key)));
}

/**
 * Everything a search reads on a topic: its heading and its prose.
 *
 * CAOS-1916 — the prose is read through {@link guidanceProseWords}, so an article that names a
 * control inline with `{anchor:<role>}` is searched by the words the reader can see rather than by
 * the token they cannot. Two things would break without it: a phrase spanning a glyph would stop
 * matching, and a query happening to contain the word "anchor" would match every annotated article.
 */
function haystack(topic: ShellGuidanceTopic): string {
  return `${topic.title} ${guidanceProseWords(topic.body)}`.toLowerCase();
}

/**
 * The articles a SEARCH finds: every topic the reader may see whose words match, wherever it is
 * scoped and whether or not something supersedes it.
 *
 * Both of those omissions are the point.
 *
 * Ignoring `scope` is the criterion itself — "search finds a topic off the current surface". A guide
 * that could only search what was already on the screen in front of you would be a filter over a
 * list you can already read.
 *
 * Ignoring superseding is the other half of what "supersedes" was defined to mean: it hides an
 * article beside a screen, it does not remove it from the org. The platform's article is still
 * installed and still owned by its package, so somebody looking for it finds it — and an
 * administrator comparing the two can see both, which is the only way to tell whether the
 * replacement was a good idea.
 *
 * An empty or whitespace query matches nothing rather than everything: this answers "what did you
 * search for", and a blank box has not asked anything. The panel draws its ordinary list then.
 */
export function topicsMatchingSearch(
  topics: readonly ShellGuidanceTopic[],
  query: string,
  reader: GuidanceReader,
): readonly ShellGuidanceTopic[] {
  const q = query.trim().toLowerCase();
  if (q === '') return [];
  return customerFirst(topics.filter((t) => topicVisibleTo(t, reader) && haystack(t).includes(q)));
}
